// Loading splash + texture generation + backend init.
// (UI_REDESIGN_PLAN U3 — boot scene with min display time so the splash
// doesn't flash during fast loads.)
//
// Portal-launch budget: the boot download must stay small (CrazyGames
// measures everything fetched before first gameplay). Heavy assets are
// therefore NOT preloaded here:
//   - campaign-atlas pages  → lazy-loaded per page by MapSelectScene
//   - music tracks          → lazy-loaded per track by Sound.playMusic()
// Painted art ships as resized WebP (see scripts/compress-assets.mjs).
import Phaser from "phaser";
import { goTo } from "../ui/sceneTransition";
import { generateTextures } from "../assets/generateTextures";
import { initBackend } from "../backend";
import { GAME_HEIGHT, GAME_WIDTH, AUDIO, TOWERS, ENEMIES, MAX_TOWER_LEVEL, WALK_FRAME_SUFFIXES } from "../config";
import { THEME } from "../ui/theme";
import { achievements } from "../systems/Achievements";
import { quests } from "../systems/Quests";
import { sound } from "../systems/Sound";
import { SKILL_NODES } from "../systems/SkillTree";
import { COA_COUNT } from "../ui/avatar";
import { portal } from "../portal/portal";

const MIN_SPLASH_MS = 600;

export class BootScene extends Phaser.Scene {
  private barFill: Phaser.GameObjects.Rectangle | null = null;
  private barWidth = 480;

  constructor() { super("BootScene"); }

  preload(): void {
    // The static HTML loader in index.html covered the page while the JS
    // bundle downloaded; from here the canvas exists, so the in-canvas
    // progress bar takes over.
    document.getElementById("boot-loader")?.remove();
    portal.loadingStart();
    this.drawLoadingUi();

    // IMPORTANT: do NOT generate procedural textures here. Phaser's
    // TextureManager refuses to overwrite an existing key, so any procedural
    // texture queued in preload would silently block the painted PNGs below
    // from loading. We queue all painted assets here, then call
    // generateTextures(this) at the start of create() so procedural drawers
    // only fill in keys that have no painted PNG (or where a PNG failed to
    // load).
    //
    // Raster art assets served from /public/ui/.
    this.load.image("bg-lobby", "ui/lobbybackground.webp");
    // Tutorial guide portraits: idle = woman, speaking = womanspeaking.
    // Legacy "portrait-guide" alias points at the idle portrait for back-compat.
    this.load.image("portrait-idle", "ui/woman.webp");
    this.load.image("portrait-speaking", "ui/womanspeaking.webp");
    this.load.image("portrait-guide", "ui/woman.webp");
    // Campaign-atlas pages (map_123/456/789 + frozen variants) are lazy-loaded
    // by MapSelectScene.ensurePageTexture — see PAGE_TEXTURE_FILES there.
    //
    // Painted campaign rating stars override the procedural fallbacks.
    this.load.image("star-filled", "ui/star-filled.png");
    this.load.image("star-empty", "ui/star-empty.png");
    this.load.image("icon-settings", "ui/settings.webp");
    this.load.image("frame-achievement-border", "ui/achievements/achievement-border.webp");
    this.load.image("progress-indicator", "ui/achievements/progress-indicator.webp");
    this.load.image("icon-compass", "ui/achievements/compass.webp");
    this.load.image("icon-swords", "ui/achievements/swords.webp");
    this.load.image("icon-skull", "ui/achievements/skull.webp");
    this.load.image("icon-trophy", "ui/achievements/trophy.webp");
    this.load.image("icon-book", "ui/achievements/book.webp");
    this.load.image("icon-globe", "ui/achievements/globus.webp");
    this.load.image("icon-shield", "ui/achievements/shield.webp");
    this.load.image("icon-coin", "ui/achievements/coin.webp");
    this.load.image("bg-castle", "ui/achievements/castle-bg.webp");
    // Quest board redesign: shared medieval border frame + painted level
    // banners and iconography. No WebP variants exist for these yet — add
    // them via scripts/compress-assets.mjs and swap the paths below when they do.
    this.load.image("frame-medieval-border", "ui/medieval-border.png");
    this.load.image("bg-quest-sidebar", "ui/quests/sidebar-background.png");
    this.load.image("flag-quest-green", "ui/quests/flag-green.png");
    this.load.image("flag-quest-blue", "ui/quests/flag-blue.png");
    this.load.image("flag-quest-brown", "ui/quests/flag-brown.png");
    this.load.image("icon-quest-tower", "ui/quests/tower.png");
    this.load.image("icon-quest-lock", "ui/quests/lock-outlined.png");
    this.load.image("icon-quest-lock-filled", "ui/quests/lock-filled.png");
    this.load.image("icon-quest-check", "ui/quests/check-mark.png");
    this.load.image("icon-quest-state-done", "ui/quests/completed.png");
    this.load.image("icon-quest-state-progress", "ui/quests/hourglass.png");
    this.load.image("icon-quest-state-locked", "ui/quests/lock.png");
    this.load.svg("icon-quest-coins", "ui/quests/coins.svg");
    for (const mapKey of [
      "greenwold_pass", "stonebridge", "frostfell_gorge",
      "whispering_woods", "dragons_spine", "sunken_marsh",
      "goldmere_coast", "ironforge_app", "twilight_chasm"
    ]) {
      this.load.image(`map-preview-${mapKey}`, `ui/map-previews/${mapKey}.webp`);
    }

    // Load pre-made Coat of Arms images
    for (let i = 1; i <= COA_COUNT; i++) {
      this.load.image(`coa-${i}`, `coat of arms/coa-${i}.webp`);
    }

    // UI 9-slice frames remain procedural; map backgrounds are composed from
    // tiles + decorations by MapRenderer.

    // Painted six-frame walk cycles. Each texture is already normalized to
    // the unit's natural render size; Enemy.ts deliberately applies no scale.
    for (const enemy of Object.values(ENEMIES)) {
      for (const suffix of WALK_FRAME_SUFFIXES) {
        this.load.image(
          `enemy-${enemy.key}${suffix}`,
          `enemies/enemy-${enemy.key}${suffix}.png`,
        );
      }
    }

    // Tower textures for all levels 1..MAX_TOWER_LEVEL (painted PNGs override
    // procedural fallbacks). Missing files are tolerated by the loaderror handler.
    for (const t of Object.values(TOWERS)) {
      for (let lvl = 1; lvl <= MAX_TOWER_LEVEL; lvl++) {
        this.load.image(`tower-${t.key}-${lvl}`, `towers/tower-${t.key}-${lvl}.png`);
      }
    }

    // Painted weapon turrets for the weapon towers: three material tiers each;
    // crossbow and archer additionally ship a "-fired" pose per tier.
    for (const fam of ["crossbow", "cannon", "archer"]) {
      for (const tier of [1, 2, 3]) {
        this.load.image(`turret-${fam}-t${tier}`, `towers/turret-${fam}-t${tier}.png`);
        if (fam !== "cannon") {
          this.load.image(`turret-${fam}-t${tier}-fired`, `towers/turret-${fam}-t${tier}-fired.png`);
        }
      }
    }

    // Projectile textures.
    this.load.image("proj-arrow", "projectiles/proj-arrow.png");
    this.load.image("proj-cannon", "projectiles/proj-cannon.png");
    this.load.image("proj-mage", "projectiles/proj-mage.png");
    this.load.image("proj-frost", "projectiles/proj-frost.png");
    this.load.image("proj-poison", "projectiles/proj-poison.png");
    this.load.image("proj-bolt", "projectiles/proj-bolt.png");

    // Painted flight frames B-F for the round projectiles: the trail animates,
    // the head stays fixed. Projectile plays any `-b`..`-f` frames that exist.
    for (const key of ["cannon", "mage", "frost", "poison"]) {
      for (const s of WALK_FRAME_SUFFIXES.slice(1)) {
        this.load.image(`proj-${key}${s}`, `projectiles/proj-${key}${s}.png`);
      }
    }

    const towerVfx = [
      "archer-muzzle", "archer-trail", "cannon-muzzle", "cannon-explosion",
      "mage-muzzle", "mage-impact", "frost-muzzle", "frost-splash",
      "lightning-sparks", "lightning-bolt", "poison-muzzle", "poison-cloud",
      "crossbow-trail", "holy-beam", "holy-ground-ring", "holy-speed-aura"
    ];
    for (const key of towerVfx) this.load.image(`vfx-${key}`, `vfx/towers/${key}.png`);

    // Commander hotbar icons and cast effects.
    for (const key of ["rally", "meteor", "heal", "conscript"]) {
      this.load.image(`ability-${key}`, `abilities/ability-${key}.png`);
    }
    for (const key of [
      "rally-banner", "meteor-impact", "heal-beam", "heal-shield",
      "conscript-coins", "conscript-circle"
    ]) {
      this.load.image(`ability-vfx-${key}`, `abilities/${key}.png`);
    }

    // Skill Tree UI Background and Icons.
    // Only these node ids have hand-painted art in public/ui/skills/ — the
    // rest already have bespoke procedural drawers in generateTextures.ts, so
    // skip queuing a load for them (a missing-file load still resolves 200
    // via Vite's dev-server SPA fallback, which fails PNG decoding and logs
    // a "Failed to process file" console error instead of a clean 404).
    const PAINTED_SKILL_ICONS = new Set([
      "crown.startGold", "crown.waveBonus", "crown.interest", "crown.firstKill", "crown.sellRefund",
      "forge.range", "forge.damage", "forge.fireRate", "forge.discount", "forge.upgradeBoost",
      "banner.rally", "banner.meteor", "banner.heal", "banner.conscript", "banner.faster",
    ]);
    this.load.image("bg-skilltree", "ui/bg-skilltree.webp");
    for (const node of SKILL_NODES) {
      if (!PAINTED_SKILL_ICONS.has(node.id)) continue;
      const fileId = node.id.replace(".", "-");
      this.load.image(`skill-${node.id}`, `ui/skills/skill-${fileId}.webp`);
    }

    // Tile textures (used directly AND composed by MapRenderer to build the
    // campaign map backgrounds tile-by-tile from the MapDef waypoints +
    // decorations + blocked sets).
    this.load.image("tile-grass", "tiles/tile-grass.png");
    this.load.image("tile-grass-alt", "tiles/tile-grass-alt.png");
    this.load.image("tile-path", "tiles/tile-path.png");
    this.load.image("tile-rock", "tiles/tile-rock.png");
    this.load.image("tile-fountain", "tiles/tile-fountain.png");
    this.load.image("tile-torch", "tiles/tile-torch.png");
    this.load.image("tile-banner", "tiles/tile-banner.png");
    this.load.image("tile-pine", "tiles/tile-pine.png");
    this.load.image("tile-water", "tiles/tile-water.png");
    this.load.image("tile-ice-shard", "tiles/tile-ice-shard.png");
    this.load.image("tile-castle-gate", "tiles/tile-castle-gate.png");

    // Real audio (low-cortisol acoustic medieval). Files are optional —
    // missing files are tolerated by Sound.ts (it tracks loaded keys).
    // Suppress loader errors so a missing OGG (or other optional asset)
    // doesn't halt boot.
    this.load.on("loaderror", (file: Phaser.Loader.File) => {
      if (file.type === "audio") console.info(`[boot] audio missing: ${file.key}`);
      else if (file.type === "image") console.info(`[boot] image missing: ${file.key}`);
    });
    // Only SFX + ambience load at boot (~1 MB). Music tracks (AUDIO.music,
    // ~29 MB) are fetched on demand by Sound.playMusic()/setBossLayer().
    const flatten = (obj: Record<string, unknown>): string[] => {
      const out: string[] = [];
      for (const v of Object.values(obj)) {
        if (typeof v === "string") out.push(v);
        else if (Array.isArray(v)) for (const item of v) { if (typeof item === "string") out.push(item); }
        else if (v && typeof v === "object") out.push(...flatten(v as Record<string, unknown>));
      }
      return out;
    };
    for (const path of [
      ...flatten(AUDIO.sfx as unknown as Record<string, unknown>),
      ...flatten(AUDIO.ambience as unknown as Record<string, unknown>)
    ]) {
      this.load.audio(path, path);
    }
  }

  /** Splash drawn at preload start with plain rectangles + text only — the
   *  procedural THEME textures don't exist until generateTextures() runs in
   *  create(), so nothing here may reference a texture. */
  private drawLoadingUi(): void {
    this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, THEME.hex.ironDeep).setOrigin(0);

    this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 60, "MEDIEVAL TOWER DEFENSE", {
      fontFamily: THEME.fonts.display, fontSize: "40px",
      color: THEME.colors.parchment, stroke: THEME.colors.ironDeep, strokeThickness: 4
    }).setOrigin(0.5);

    this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 20, "Forging the realm…", {
      fontFamily: THEME.fonts.body, fontSize: "16px", color: THEME.colors.parchmentDim
    }).setOrigin(0.5);

    // Progress bar frame + gold fill, driven by real loader progress.
    const barW = this.barWidth, barH = 28;
    const barX = (GAME_WIDTH - barW) / 2, barY = GAME_HEIGHT / 2 + 20;
    this.add.rectangle(barX, barY, barW, barH, THEME.hex.ironDeep)
      .setOrigin(0)
      .setStrokeStyle(2, THEME.hex.gold, 0.8);
    this.barFill = this.add.rectangle(barX + 6, barY + 6, 4, barH - 12, THEME.hex.gold).setOrigin(0);
    this.load.on("progress", (p: number) => {
      this.barFill?.setSize(Math.max(4, (barW - 12) * p), barH - 12);
    });
  }

  create(): void {
    const startedAt = performance.now();

    // Procedural fallback pass — runs AFTER the loader so painted PNGs from
    // /public (towers, enemies, projectiles, tiles, frames, maps) win, and
    // generateTextures() only fills in any key that lacks a painted asset or
    // whose load failed. Order matters: BootScene.create() and every later
    // scene reads textures only from this point on.
    generateTextures(this);

    // Attach Sound system to this scene's SoundManager. Subsequent scenes
    // share the same global manager (Phaser.Sound.BaseSoundManager is global).
    sound.attach(this);

    // Loader is done — pin the bar full for the min-splash beat.
    this.barFill?.setSize(this.barWidth - 12, 16);

    // Fire-and-forget backend init; advance to menu after both backend and
    // min splash time have elapsed.
    let backendDone = false;
    let timerDone = false;
    const tryAdvance = () => {
      // No crest on the very first curtain: the save (and so the player's
      // heraldry) may still be empty on a cold boot.
      if (backendDone && timerDone) goTo(this, "MenuScene", { crest: false });
    };
    void initBackend().finally(() => { backendDone = true; tryAdvance(); });
    achievements.init();
    quests.init();
    const elapsed = performance.now() - startedAt;
    const remaining = Math.max(0, MIN_SPLASH_MS - elapsed);
    this.time.delayedCall(remaining, () => { timerDone = true; tryAdvance(); });
  }
}
